"use client";

import { useState } from "react";
import clsx from "clsx";
import { resolveDossier } from "@/lib/sign/dossierManifest";
import { SignDocumentModal } from "@/components/sign/SignDocumentModal";
import { ShareDossierModal } from "@/components/sign/ShareDossierModal";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * TradeDossier — inline strip mounted at the bottom of each Reveal scene
 * (T1Reveal / T2Reveal / T3Reveal). Surfaces all the trade's PDFs as a
 * row of clickable tiles + a "Share dossier" CTA.
 *
 *   Click a tile  → opens SignDocumentModal in viewOnly mode.
 *   Click Share   → opens ShareDossierModal (decorative).
 *
 * The D hotkey re-opens the most-recently-viewed doc via the existing
 * SignDocContext registration in SignDocumentModal.
 */
export function TradeDossier({
  scenarioId,
  className,
}: {
  scenarioId: string;
  className?: string;
}) {
  const dossier = resolveDossier(scenarioId);
  const [openDocKey, setOpenDocKey] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const openDoc = dossier.docs.find((d) => d.key === openDocKey) ?? null;
  const t = useTranslations("tradeDossier");

  return (
    <div className={clsx("glass flex flex-col gap-3 p-4", className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
            {t("header")}
          </p>
          <p className="font-display text-[13px] font-semibold text-orbitra-paper">
            <span dir="ltr">{dossier.tradeRef}</span>
            <span className="ms-2 font-sans text-[11px] font-normal text-orbitra-muted" dir="ltr">
              {dossier.parties}
            </span>
          </p>
          <p className="truncate text-[10px] text-orbitra-muted">{dossier.summary}</p>
        </div>
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          className="cta-primary shrink-0 px-4 py-2 font-display text-[12px] font-semibold"
        >
          {t("shareCta")}
        </button>
      </div>

      <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-7">
        {dossier.docs.map((d) => (
          <li key={d.key}>
            <button
              type="button"
              onClick={() => setOpenDocKey(d.key)}
              className="flex h-full w-full flex-col gap-1 rounded-md border border-state-verified/25 bg-orbitra-ink/40 px-2.5 py-2 text-start transition-state hover:-translate-y-px hover:border-state-verified/55 hover:bg-state-verified/8 hover:shadow-[0_0_12px_var(--state-verified-glow)] focus:outline-none focus-visible:ring-2 focus-visible:ring-state-verified/60"
              aria-label={`Open ${d.label} PDF`}
            >
              <span aria-hidden="true" className="font-mono text-[14px] text-state-verified">📄</span>
              <span className="text-[11px] font-semibold leading-tight text-orbitra-paper">
                {d.label}
              </span>
              <span className="mt-auto truncate text-[9px] text-orbitra-muted">
                {d.issuerName}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {openDoc && (
        <SignDocumentModal
          id={`dossier:${scenarioId}:${openDoc.key}`}
          open={openDocKey === openDoc.key}
          onOpen={() => setOpenDocKey(openDoc.key)}
          onClose={() => setOpenDocKey(null)}
          pdfPath={openDoc.pdfPath}
          documentTitle={openDoc.label}
          documentRef={dossier.tradeRef}
          signedAs={openDoc.issuerName}
          signerOrg={openDoc.issuerOrg}
          viewOnly
        />
      )}

      <ShareDossierModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        tradeRef={dossier.tradeRef}
      />
    </div>
  );
}
